/**
 * Shared status color mapping for analysis indicator blocks.
 *
 * Used by MultiDayAnalysis, YearlyAnalysis, FiveYearAnalysis, and AlarmStatus
 * to ensure consistent color coding across all diagnostic sections.
 */

export const STATUS_COLORS = {
  critical: {
    bg: 'tw-bg-red-50',
    icon: 'tw-text-red-500',
    text: 'tw-text-red-700',
    sub: 'tw-text-red-500',
    border: 'tw-border-red-100',
  },
  warning: {
    bg: 'tw-bg-amber-50',
    icon: 'tw-text-amber-500',
    text: 'tw-text-amber-700',
    sub: 'tw-text-amber-500',
    border: 'tw-border-amber-100',
  },
  moderate: {
    bg: 'tw-bg-yellow-50',
    icon: 'tw-text-yellow-600',
    text: 'tw-text-yellow-700',
    sub: 'tw-text-yellow-600',
    border: 'tw-border-yellow-100',
  },
  good: {
    bg: 'tw-bg-green-50',
    icon: 'tw-text-green-500',
    text: 'tw-text-green-700',
    sub: 'tw-text-green-500',
    border: 'tw-border-green-100',
  },
  neutral: {
    bg: 'tw-bg-gray-50',
    icon: 'tw-text-gray-400',
    text: 'tw-text-gray-600',
    sub: 'tw-text-gray-400',
    border: 'tw-border-gray-100',
  },
}

/**
 * Convert a percentage ratio to a status level.
 * @param {number|null} ratio - Percentage (e.g., 85 means 85%)
 * @returns {string} Status level key
 */
export function statusForRatio(ratio) {
  if (ratio == null) return 'neutral'
  if (ratio < 50) return 'critical'
  if (ratio < 75) return 'warning'
  if (ratio < 90) return 'moderate'
  return 'good'
}

/**
 * Get Tailwind bar color class for a ratio value.
 */
export function ratioBarColor(ratio) {
  if (ratio < 50) return 'tw-bg-red-400'
  if (ratio < 75) return 'tw-bg-amber-400'
  if (ratio < 90) return 'tw-bg-yellow-400'
  return 'tw-bg-green-400'
}

/**
 * Get Tailwind text color class for a ratio value.
 */
export function ratioTextColor(ratio) {
  if (ratio < 50) return 'tw-text-red-600'
  if (ratio < 75) return 'tw-text-amber-600'
  if (ratio < 90) return 'tw-text-amber-500'
  if (ratio <= 110) return 'tw-text-green-600'
  return 'tw-text-blue-600'
}

/**
 * Helper to get a specific color property for a status level.
 * Usage: colorFor('performance', 'bg') or colorFor(statusForRatio(85), 'text')
 */
export function colorFor(level, prop) {
  return STATUS_COLORS[level]?.[prop] || STATUS_COLORS.neutral[prop]
}
